import { useState, useEffect } from "react";
import { ChevronDown, Home, Coffee, User, MessageCircle, Link, QrCode, CheckCircle2, Loader2 } from "lucide-react";
import { Toggle } from "@/components/Toggle";
import { 
  useGuestAppConfig, 
  useUpsertGuestAppConfig,
  type GuestAppConfig 
} from "@/features/configurazione";
import { usePermissions } from "@/features/collaboratori";

const sel = "appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30";
const TABS = ["Generale", "Contenuti", "Chatbot", "Accesso"];

// Hook per simulare strutture (fino a migration completa)
function useMockStrutture() {
  return {
    keys: ['Struttura Demo'],
    get: (key: string) => ({
      location: 'Milano, Italia',
      wifi: 'HeditaGuest2024',
      servizi: ['WiFi', 'Colazione', 'Parcheggio'],
      dotazioni: ['Aria Condizionata', 'Smart TV'],
      host: { nome: 'Marco' },
      regole: ['Check-in 15:00', 'Check-out 11:00'],
      knowledgeBase: ''
    })
  };
}

function Row({ label, sub, children }: { label: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-border last:border-0">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
      {children && <div className="flex-shrink-0 ml-4">{children}</div>}
    </div>
  );
}

function InfoTag({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-muted/50 text-muted-foreground px-2.5 py-1 rounded-full">
      <CheckCircle2 className="h-3 w-3 text-emerald-500 flex-shrink-0" />{label}
    </span>
  );
}

export default function GuestAppConfig() {
  const { adminId, isLoading: isLoadingPerms } = usePermissions();
  const mockStore = useMockStrutture();
  const keys = mockStore.keys;
  const [tab, setTab] = useState(0);
  const [strutturaKey, setStrutturaKey] = useState(keys[0]);
  const struttura = mockStore.get(strutturaKey);
  
  // Fetch config da Supabase
  const { data: config, isLoading: isLoadingConfig } = useGuestAppConfig(adminId || undefined);
  const upsertConfig = useUpsertGuestAppConfig();

  const [showServizi, setShowServizi] = useState(true);
  const [showDotazioni, setShowDotazioni] = useState(true);
  const [showHost, setShowHost] = useState(true);
  const [showRecensioni, setShowRecensioni] = useState(true);
  const [showSegnalazioni, setShowSegnalazioni] = useState(true);
  const [showChatbot, setShowChatbot] = useState(true);
  const [modalitaAccesso, setModalitaAccesso] = useState<"link" | "qr" | "entrambi">("entrambi");
  const [invioAuto, setInvioAuto] = useState("3 giorni prima");
  const [durataAccesso, setDurataAccesso] = useState("Durata soggiorno + 1 giorno");
  const guestLink = `https://guest.hedita.app/${strutturaKey}/OSPITE123`;
  const [knowledgeBase, setKnowledgeBase] = useState('');
  
  // Sync con dati dal server
  useEffect(() => {
    if (config?.chatbot_knowledge_base !== undefined) {
      setKnowledgeBase(config.chatbot_knowledge_base);
    }
  }, [config]);

  const handleStrutturaChange = (k: string) => {
    setStrutturaKey(k);
    // Reset knowledge base per la nuova struttura (carica da config)
    setKnowledgeBase(config?.chatbot_knowledge_base || '');
  };

  const handleKbChange = (v: string) => {
    setKnowledgeBase(v);
    // Salva su Supabase
    if (adminId) {
      upsertConfig.mutate({
        adminId,
        config: {
          property_id: strutturaKey, // TODO: usare ID reale
          chatbot_knowledge_base: v,
          chatbot_attivo: showChatbot
        }
      });
    }
  };
  
  if (isLoadingPerms || isLoadingConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Configurazione Guest App</h1>
        <p className="text-sm text-muted-foreground mt-0.5">La Guest App si popola automaticamente con i dati inseriti in Struttura e Camere.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-border">
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              className="flex-1 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-colors"
              style={{
                color: tab === i ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
                borderBottom: tab === i ? "2px solid hsl(var(--primary))" : "2px solid transparent",
              }}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5">

          {/* ── GENERALE ── */}
          {tab === 0 && (
            <div>
              <Row label="Struttura attiva" sub="I dati vengono letti automaticamente dalla scheda struttura">
                <div className="relative">
                  <select value={strutturaKey} onChange={e => handleStrutturaChange(e.target.value)} className={sel}>
                    {keys.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                </div>
              </Row>
              <div className="mt-4 bg-muted/20 rounded-xl p-4">
                <p className="text-xs font-medium text-muted-foreground mb-2.5">Dati letti automaticamente da questa struttura</p>
                <div className="flex flex-wrap gap-2">
                  <InfoTag label={struttura.location} />
                  <InfoTag label={`Wi-Fi: ${struttura.wifi}`} />
                  <InfoTag label={`${struttura.servizi.length} servizi`} />
                  <InfoTag label={`${struttura.dotazioni.length} dotazioni`} />
                  <InfoTag label={`Host: ${struttura.host.nome}`} />
                  <InfoTag label={`${struttura.regole.length} regole casa`} />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Aggiungi o modifica i dati dalla sezione <span className="font-medium text-foreground">Strutture → Camere</span>.
                </p>
              </div>
            </div>
          )}

          {/* ── CONTENUTI ── */}
          {tab === 1 && (
            <div>
              <Row label="Struttura" sub="Info generali, dotazioni, regole, Wi-Fi">
                <div className="flex items-center gap-2"><Home className="h-3.5 w-3.5 text-muted-foreground" /><Toggle value={true} onChange={() => {}} /></div>
              </Row>
              <Row label="Host" sub="Profilo, bio, contatti e valutazioni">
                <div className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-muted-foreground" /><Toggle value={showHost} onChange={() => setShowHost(v => !v)} /></div>
              </Row>
              <Row label="Servizi" sub="Grid con orari e dettagli di ogni servizio">
                <div className="flex items-center gap-2"><Coffee className="h-3.5 w-3.5 text-muted-foreground" /><Toggle value={showServizi} onChange={() => setShowServizi(v => !v)} /></div>
              </Row>
              <Row label="Chat AI" sub="Chatbot che risponde alle domande degli ospiti">
                <div className="flex items-center gap-2"><MessageCircle className="h-3.5 w-3.5 text-muted-foreground" /><Toggle value={showChatbot} onChange={() => setShowChatbot(v => !v)} /></div>
              </Row>
              <Row label="Dotazioni" sub="Lista dotazioni presenti in struttura">
                <Toggle value={showDotazioni} onChange={() => setShowDotazioni(v => !v)} />
              </Row>
              <Row label="Recensioni" sub="Permetti agli ospiti di lasciare una recensione">
                <Toggle value={showRecensioni} onChange={() => setShowRecensioni(v => !v)} />
              </Row>
              <Row label="Segnalazioni" sub="Permetti agli ospiti di segnalare problemi">
                <Toggle value={showSegnalazioni} onChange={() => setShowSegnalazioni(v => !v)} />
              </Row>
            </div>
          )}

          {/* ── CHATBOT ── */}
          {tab === 2 && (
            <div className="space-y-4">
              <div className="bg-muted/20 rounded-xl p-4">
                <p className="text-xs font-medium text-foreground mb-2">Fonti usate automaticamente</p>
                <div className="flex flex-wrap gap-2">
                  <InfoTag label="Info struttura" />
                  <InfoTag label="Servizi e orari" />
                  <InfoTag label="Profilo host" />
                  <InfoTag label="Regole casa" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground block mb-1.5">Note aggiuntive per il chatbot</label>
                <textarea rows={6} value={knowledgeBase} onChange={e => handleKbChange(e.target.value)}
                  placeholder="Es: il codice cassaforte è 1234 · il parcheggio è in via Roma 5 · supermercato a 300m…"
                  className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                <p className="text-xs text-muted-foreground mt-1.5">Il chatbot cercherà qui le risposte non trovate nei dati struttura/host/servizi.</p>
              </div>
            </div>
          )}

          {/* ── ACCESSO ── */}
          {tab === 3 && (
            <div className="space-y-4">

              {/* Modalità condivisione */}
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Modalità di condivisione</p>
                <p className="text-xs text-muted-foreground mb-3">Come vuoi che l'ospite acceda alla Guest App?</p>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { id: "link",     label: "Solo Link",      sub: "Link via email/SMS",   icon: <Link className="h-4 w-4" /> },
                    { id: "qr",       label: "Solo QR Code",   sub: "QR da stampare/inviare", icon: <QrCode className="h-4 w-4" /> },
                    { id: "entrambi", label: "Link + QR",      sub: "Entrambi disponibili", icon: <><Link className="h-3.5 w-3.5" /><QrCode className="h-3.5 w-3.5" /></> },
                  ] as const).map(opt => (
                    <button key={opt.id} onClick={() => setModalitaAccesso(opt.id)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${modalitaAccesso === opt.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                      <div className={`flex items-center gap-1 ${modalitaAccesso === opt.id ? "text-primary" : "text-muted-foreground"}`}>{opt.icon}</div>
                      <span className={`text-xs font-semibold leading-tight ${modalitaAccesso === opt.id ? "text-primary" : "text-foreground"}`}>{opt.label}</span>
                      <span className="text-[10px] text-muted-foreground leading-tight">{opt.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Anteprima link */}
              {(modalitaAccesso === "link" || modalitaAccesso === "entrambi") && (
                <div className="bg-muted/30 rounded-xl p-3 border border-border">
                  <p className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5"><Link className="h-3.5 w-3.5 text-primary" /> Link di esempio</p>
                  <div className="flex items-center gap-2">
                    <code className="text-[11px] text-muted-foreground truncate flex-1 bg-background px-2 py-1 rounded-lg border border-border">{guestLink}</code>
                    <button onClick={() => navigator.clipboard.writeText(guestLink)}
                      className="text-xs text-primary hover:underline flex-shrink-0">Copia</button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5">Il link reale viene generato automaticamente per ogni ospite al momento della prenotazione.</p>
                </div>
              )}

              {/* Anteprima QR */}
              {(modalitaAccesso === "qr" || modalitaAccesso === "entrambi") && (
                <div className="bg-muted/30 rounded-xl p-3 border border-border">
                  <p className="text-xs font-medium text-foreground mb-1.5 flex items-center gap-1.5"><QrCode className="h-3.5 w-3.5 text-primary" /> QR Code</p>
                  <div className="flex items-center gap-3">
                    <div className="w-16 h-16 bg-background border border-border rounded-lg flex items-center justify-center flex-shrink-0">
                      <QrCode className="h-10 w-10 text-muted-foreground/30" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Il QR viene generato per ogni prenotazione e può essere stampato o inviato via email/WhatsApp.</p>
                      <button className="mt-1.5 text-xs text-primary hover:underline">Scarica QR di esempio</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Invio e durata */}
              <Row label="Invio automatico" sub="Giorni prima del check-in">
                <div className="relative">
                  <select value={invioAuto} onChange={e => setInvioAuto(e.target.value)} className={sel}>
                    {["3 giorni prima", "5 giorni prima", "7 giorni prima", "Solo manuale"].map(d => <option key={d}>{d}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                </div>
              </Row>
              <Row label="Durata accesso" sub="Validità del link/QR dopo il check-in">
                <div className="relative">
                  <select value={durataAccesso} onChange={e => setDurataAccesso(e.target.value)} className={sel}>
                    {["Durata soggiorno + 1 giorno", "Solo durante il soggiorno", "7 giorni dal check-in", "Illimitato"].map(d => <option key={d}>{d}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                </div>
              </Row>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
